import { makeAutoObservable, runInAction } from 'mobx';
import { Base, t } from 'zf-web-components';
import { Common } from 'zf-types';
import { LightCache } from 'zf-web-components/widgets';
import { DOMAIN } from 'config';
import { LoginService } from './services';
import { WeComLoginService } from './services/wecom';
import { DingTalkLoginService } from './services/dingtalk';
import type * as Types from './types';
import type { RootStore } from '@stores';
import type { StoreOptions } from '@hooks';
import type { FormValues } from './components/form';

export class LoginStore {

  loginMethod: Common.LoginMethod = Common.LoginMethod.PASSWORD;
  codeInputVisible = this.isPeiXun;
  qrCodeVisible = false;
  captchaToken?: string;
  captchaImage = '';
  tenant: string;
  routeTenant?: string;

  isLoginBtnLoading = false;
  isCaptchaLoading = false;

  params: Types.LoginPageParams;
  private readonly root: RootStore;
  private readonly service: LoginService;
  private readonly wecomService: WeComLoginService;
  private readonly dingtalkService: DingTalkLoginService;
  private readonly navigate: StoreOptions['navigate'];
  readonly loginMethodMap = {
    [Common.LoginMethod.PASSWORD]: t('密码登录'),
    [Common.LoginMethod.DINGTALK]: t('钉钉登录'),
    [Common.LoginMethod.WECOM]: t('企业微信登录'),
    [Common.LoginMethod.FEISHU]: t('飞书登录'),
    [Common.LoginMethod.SMS]: t('短信登录')
  };

  constructor({ root, params, navigate }: StoreOptions<Types.LoginPageParams>) {
    this.root = root;
    this.params = params;
    this.navigate = navigate;
    this.tenant = root.local.code ?? '';
    makeAutoObservable(this);
    this.service = new LoginService();
    this.wecomService = new WeComLoginService();
    this.dingtalkService = new DingTalkLoginService();
    // 配置自动钉钉登录
    if (params.tenant != null) {
      this.routeTenant = params.tenant;
    }
  }

  async onLoad(): Promise<void> {
    if (this.routeTenant == null) {
      return;
    }
    const data = await this.dingtalkService.autoLogin({ tenant: this.routeTenant, app: this.getCurrentApp() });
    if (data == null) {
      return;
    }
    // 登录成功
    if (data.bizCode == null) {
      await this.loginSuccess(data);
      return;
    }

    await this.loginFailed(data);
  }

  get isPeiXun(): boolean {
    return this.getCurrentApp() === 'training';
  }

  get loginMethodName(): string {
    return this.loginMethodMap[this.loginMethod];
  }

  get isPasswordLogin(): boolean {
    return this.loginMethod === Common.LoginMethod.PASSWORD;
  }

  get isDingtalkLogin(): boolean {
    return this.loginMethod === Common.LoginMethod.DINGTALK;
  }

  async onOtherLoginClick(value: Common.LoginMethod): Promise<void> {
    runInAction(() => {
      this.loginMethod = value;
    });
  }

  onBackClick(): void {
    this.loginMethod = Common.LoginMethod.PASSWORD;
    this.qrCodeVisible = false;
  }

  async fetchCaptcha(): Promise<void> {
    const { png, token } = await this.service.captcha();
    runInAction(() => {
      this.captchaImage = png;
      this.captchaToken = token;
    });
  }

  onInputKeyUp(event: React.KeyboardEvent<HTMLInputElement>, form: Base.FormInstance<FormValues>): void {
    if (event.key === 'Enter') {
      this.onLoginButtonClick(form);
    }
  }
  async onLoginButtonClick(form: Base.FormInstance<FormValues>): Promise<void> {
    try {
      const formValues = await form.validateFields();

      if (!['gz-saizeriya', 'hmyz', 'hanyan', 'demo'].includes(formValues?.code) && this.isPeiXun) {
        Base.message.error(t('无法使用新登录画面登录，即将进入旧版画面'));
        // 跳转旧登录画面
        setTimeout(() => {
          window.location.href = '/login.html';
        }, 1000);
        return;
      }
      this.isLoginBtnLoading = true;

      if (this.loginMethod === Common.LoginMethod.WECOM) {
        this.initWeComQrcode();
        return;
      }

      if (this.loginMethod === Common.LoginMethod.DINGTALK) {
        this.initDingtalkQrcode();
        return;
      }

      const data = await this.service.login({
        ...formValues,
        captchaToken: this.captchaToken,
        app: this.getCurrentApp()
      });

      // 登录成功
      if (data.bizCode == null) {
        await this.loginSuccess(data);
        return;
      }

      await this.loginFailed(data);
    } finally {
      runInAction(() => {
        this.isLoginBtnLoading = false;
      });
    }
  }

  getValidationRules(name: keyof FormValues): Base.Rule[] {
    const validateRules = {
      code: [
        { required: this.isPeiXun || this.codeInputVisible, message: t('请输入企业码') },
        {
          pattern: /^[a-zA-Z0-9_-]*$/,
          message: t('企业码只能包含字母、数字、下划线和短横线')
        }
      ],
      id: [{ required: true, message: t('请输入用户名') }],
      password: [
        { required: true, message: t('请输入密码') },
        { min: 4, message: t('请输入最少4位字符') }
      ],
      captcha: [{ required: true, message: t('请输入验证码') }]
    };

    return validateRules[name];
  }

  onValuesChange({ code }: Partial<FormValues>): void {
    if (code == null) {
      return;
    }
    this.tenant = code;
    this.root.local.code = code;
  }

  private async loginSuccess({ accessToken, refreshToken, expiresIn }: Types.LoginResponse): Promise<void> {
    this.root.local.token = accessToken;
    this.root.local.refreshToken = refreshToken;
    this.root.local.expireAt = Date.now() + (expiresIn * 1000);
    await LightCache.clearAll();
    await this.root.refreshUser();
    if (this.params.redirect == null) {
      this.navigateTo('/');
      return;
    }
    const url = new URL(this.params.redirect);
    if (url.hostname !== window.location.hostname) {
      this.navigateTo('/');
      return;
    }
    if (url.search.length > 0) {
      window.location.href = this.params.redirect;
      return;
    }
    this.navigateTo(url.pathname);
  }

  private navigateTo(path: string): void {
    // 适配移动端，将菜单折叠
    if (window.innerWidth < 768) {
      this.root.shared.setCollapsed(true);
    }
    this.navigate(path);
  }

  hexToRgb(hex: string, alpha: number): string {
    // 去掉开头的#
    const formatHex = hex.replace('#', '');

    // 解析红色、绿色和蓝色部分
    const red = parseInt(formatHex.slice(0, 2), 16);
    const green = parseInt(formatHex.slice(2, 4), 16);
    const blue = parseInt(formatHex.slice(4, 6), 16);

    // 返回RGB格式的字符串
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  /**
   * 登录失败处理
   */
  private async loginFailed(data: Types.LoginResponse): Promise<void> {
    // 账号密码错误
    if (data.bizCode === 'B01') {
      Base.message.error(t('登录失败，请检查用户名或密码是否正确'));
      return;
    }

    if (data.bizCode === 'B03') {
      Base.message.error(t('登录失败，请检查用户名或密码是否正确'));
      this.fetchCaptcha();
      return;
    }

    if (data.bizCode === 'B31') {
      Base.message.error(t('当前企业禁止使用账号密码登录'));
      return;
    }

    if (data.bizCode === 'B32') {
      Base.message.error(t('当前用户已离职'));
      return;
    }

    if (data.bizCode === 'B33') {
      Base.message.error(t('当前用户无权限访问该系统'));
      return;
    }

    // 需要输入企业码
    if (data.bizCode === 'B02') {
      runInAction(() => {
        this.codeInputVisible = true;
      });
      Base.message.info(t('请输入企业码'));
      return;
    }

    // 需要填写验证码
    if (data.bizCode === 'B10') {
      await this.fetchCaptcha();
      Base.message.info(t('请填写验证码'));
      return;
    }

    // 验证码不正确
    if (data.bizCode === 'B11') {
      await this.fetchCaptcha();
      Base.message.info(t('验证码不正确'));
      return;
    }

    // 企微登录用户不存在
    if (data.bizCode === 'B21') {
      Base.message.info(t('系统中不存在该用户'));
      this.onBackClick();
      return;
    }
  }

  /**
   * 加载企微登录二维码
   */
  private async initWeComQrcode(): Promise<void> {
    if (this.tenant.length <= 0) {
      Base.message.warning(t('请输入企业码'));
      return;
    }
    const config = await this.wecomService.fetchConfig({ tenant: this.tenant });
    if (config?.agentId == null || config?.corpId == null) {
      Base.message.warning(t('企业未开通企业微信登录'));
      return;
    }
    runInAction(() => {
      this.qrCodeVisible = true;
    });
    const { code } = await this.wecomService.init(this.tenant, config);
    const data = await this.wecomService.login({
      tenant: this.tenant,
      authCode: code,
      app: this.getCurrentApp()
    });
    if (data.bizCode == null) {
      this.loginSuccess(data);
      return;
    }
    await this.loginFailed(data);
  }

  /**
  * 加载钉钉登录二维码
  */
  private async initDingtalkQrcode(): Promise<void> {
    if (this.tenant.length <= 0) {
      Base.message.warning(t('请输入企业码'));
      return;
    }
    const config = await this.dingtalkService.fetchConfig({ tenant: this.tenant, app: this.getCurrentApp() });
    if (config?.appKey == null || config?.corpId == null) {
      Base.message.warning(t('企业未开通钉钉登录'));
      return;
    }
    runInAction(() => {
      this.qrCodeVisible = true;
    });

    const { authCode } = await this.dingtalkService.init(this.tenant, config);
    const data = await this.dingtalkService.login({
      tenant: this.tenant,
      authCode,
      app: this.getCurrentApp()
    });
    if (data.bizCode == null) {
      this.loginSuccess(data);
      return;
    }
    await this.loginFailed(data);
  }

  private getCurrentApp(): string {
    return DOMAIN[location.hostname] ?? localStorage.getItem('local-app') ?? '';
  }

}
